/**
 * COMPONENT: SignaturePad
 * Signature électronique tracée du BED (cahier L119).
 *
 * Canvas tactile ET souris (les équipages remplissent au téléphone), tracé
 * lissé, export PNG en data-URL horodatée. Pas de prestataire externe : la
 * preuve est l'image + l'horodatage, stockés avec le bulletin.
 */

import { Eraser, PenLine } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '../ui';

interface SignaturePadProps {
  /** Signature déjà tracée (data-URL PNG) — réaffichée à la reprise du lien. */
  value?: string | null;
  /** Horodatage de la signature existante. */
  signedAt?: string | null;
  onChange: (dataUrl: string | null) => void;
  label: string;
  disabled?: boolean;
}

/** Hauteur du tracé : assez généreuse pour signer au doigt. */
const CANVAS_HEIGHT = 160;

export function SignaturePad({
  value,
  signedAt,
  onChange,
  label,
  disabled = false,
}: SignaturePadProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);
  const [hasStroke, setHasStroke] = useState(Boolean(value));

  /** Prépare le contexte 2D à la densité de l'écran (sinon tracé baveux). */
  const prepareContext = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#18181b';
    return ctx;
  }, []);

  // Dimensionnement HiDPI + restitution de la signature enregistrée.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ratio = window.devicePixelRatio || 1;
    const width = canvas.clientWidth || 320;
    canvas.width = width * ratio;
    canvas.height = CANVAS_HEIGHT * ratio;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(ratio, ratio);
    ctx.clearRect(0, 0, width, CANVAS_HEIGHT);

    if (value) {
      const image = new Image();
      image.onload = () => ctx.drawImage(image, 0, 0, width, CANVAS_HEIGHT);
      image.src = value;
      setHasStroke(true);
    }
  }, [value]);

  const pointFrom = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (disabled) return;
    // Capture du pointeur : le tracé suit le doigt même hors du canvas.
    event.currentTarget.setPointerCapture(event.pointerId);
    drawingRef.current = true;
    lastPointRef.current = pointFrom(event);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current || disabled) return;
    // preventDefault : sans lui, le geste scrolle la page sur mobile.
    event.preventDefault();

    const ctx = prepareContext();
    const point = pointFrom(event);
    const last = lastPointRef.current;
    if (!ctx || !point || !last) return;

    ctx.beginPath();
    ctx.moveTo(last.x, last.y);
    ctx.lineTo(point.x, point.y);
    ctx.stroke();

    lastPointRef.current = point;
    if (!hasStroke) setHasStroke(true);
  };

  const commit = () => {
    const canvas = canvasRef.current;
    if (!canvas || !hasStroke) return;
    onChange(canvas.toDataURL('image/png'));
  };

  const handlePointerUp = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    lastPointRef.current = null;
    commit();
  };

  const handleClear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasStroke(false);
    onChange(null);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-zinc-900">
          {label}
          <span className="text-red-500 ml-1">*</span>
        </span>
        {hasStroke && !disabled && (
          <Button type="button" variant="ghost" size="sm" onClick={handleClear}>
            <Eraser className="h-3.5 w-3.5 mr-1.5" />
            Effacer
          </Button>
        )}
      </div>

      <div
        className={`relative rounded-md border bg-white ${
          hasStroke ? 'border-zinc-300' : 'border-dashed border-zinc-300'
        }`}
      >
        <canvas
          ref={canvasRef}
          role="img"
          aria-label={`Zone de signature — ${label}`}
          style={{ height: CANVAS_HEIGHT, touchAction: 'none' }}
          className={`w-full rounded-md ${disabled ? 'opacity-60' : 'cursor-crosshair'}`}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerUp}
          onPointerCancel={handlePointerUp}
        />
        {!hasStroke && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center gap-2 text-sm text-zinc-400">
            <PenLine className="h-4 w-4" />
            Signez ici avec votre doigt ou votre souris
          </div>
        )}
      </div>

      {hasStroke && signedAt && (
        <p className="text-xs text-zinc-500">
          Signé le{' '}
          {new Date(signedAt).toLocaleString('fr-FR', {
            dateStyle: 'short',
            timeStyle: 'short',
          })}
        </p>
      )}
    </div>
  );
}
